import { useEffect, useRef } from 'preact/hooks';

/**
 * Stylus writing surface.
 *
 * Pointer handling is carried over from the original app, which had it right:
 * pointer events cover pen, touch and mouse alike, and coordinates are scaled
 * through getBoundingClientRect so the drawing tracks the pen at any CSS size.
 *
 * What is gone is the `setTimeout(init, 20)` the original needed because the
 * dialog was injected as an HTML string and the canvas did not exist yet.
 */
export interface DrawCanvasHandle {
  /** PNG data-URL, or null if nothing has been drawn. */
  toDataUrl: () => string | null;
  clear: () => void;
}

export function DrawCanvas({ handleRef }: { handleRef: { current: DrawCanvasHandle | null } }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hasInk = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 3.2;
    ctx.strokeStyle = '#31405c';
    hasInk.current = false;

    let drawing = false;

    const position = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      return {
        x: (e.clientX - r.left) * (canvas.width / r.width),
        y: (e.clientY - r.top) * (canvas.height / r.height),
      };
    };

    const onDown = (e: PointerEvent) => {
      e.preventDefault();
      drawing = true;
      hasInk.current = true;
      const { x, y } = position(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
      canvas.setPointerCapture?.(e.pointerId);
    };

    const onMove = (e: PointerEvent) => {
      if (!drawing) return;
      e.preventDefault();
      const { x, y } = position(e);
      ctx.lineTo(x, y);
      ctx.stroke();
    };

    const onUp = () => {
      drawing = false;
    };

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);

    handleRef.current = {
      toDataUrl: () => (hasInk.current ? canvas.toDataURL('image/png') : null),
      clear: () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        hasInk.current = false;
      },
    };

    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onUp);
      handleRef.current = null;
    };
  }, [handleRef]);

  return (
    <div class="canvasWrap" style={{ marginTop: '9px' }}>
      <canvas ref={canvasRef} class="drawCanvas" width={900} height={330} />
    </div>
  );
}
